/**
 * Shopify Forms Custom Trigger & Dismissal Controller
 *
 * Features:
 * 1. Immediate Background Mount: Intercepts form configuration fetch to eliminate
 *    any admin delay (e.g. 9s), mounting the embed in 0ms while keeping it hidden.
 * 2. 40% Scroll Trigger: Accurately triggers when visitor reaches 40% scroll depth
 *    on desktop or mobile. Guarantees zero flash on initial load.
 * 3. 7-Day Dismissal: Suppresses popup for 7 days when closed via "X", "Maybe later",
 *    decline buttons, or Escape.
 * 4. Safe Expiration: After 7 days, suppression expires and clears stale session keys.
 * 5. Testing Helper: Append ?reset_forms=true or ?test_forms=true to URL to clear
 *    suppression for instant re-testing.
 * 6. Non-Disruptive: 100% compatible with Shopify Forms, Razorpay, and third-party apps.
 */
(function() {
  'use strict';

  // Guard against duplicate execution
  if (window.__shopifyFormsCustomTriggerInitialized) return;
  window.__shopifyFormsCustomTriggerInitialized = true;

  const STORAGE_DISMISSAL_KEY = 'shopify_forms_popup_dismissed_at';
  const STORAGE_SUBMISSION_KEY = 'shopify_forms_popup_submitted';
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000; // 604,800,000 ms (7 days)
  const SCROLL_THRESHOLD_PERCENT = 40;

  // Developer / Merchant testing override: ?reset_forms=true or ?test_forms=true
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('reset_forms') || urlParams.has('test_forms') || urlParams.has('clear_forms')) {
      localStorage.removeItem(STORAGE_DISMISSAL_KEY);
      localStorage.removeItem(STORAGE_SUBMISSION_KEY);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && (k.endsWith('-minimized-in-session') || k.endsWith('-dismissed-in-session') || k.endsWith('-dismissed-after-submit'))) {
          localStorage.removeItem(k);
        }
      }
      console.log('[Shopify Forms Trigger] Testing mode: Stored dismissal and submission state cleared.');
    }
  } catch (e) {}

  /**
   * INTERCEPTOR: Eliminate the Shopify Forms internal delay timer (e.g. 9000ms).
   * By setting delay = 0 in the fetched form configuration, Shopify Forms mounts
   * the embed immediately into the DOM. Our CSS keeps it completely hidden until
   * the visitor reaches 40% scroll, allowing instant, lag-free popup display!
   */
  try {
    const originalFetch = window.fetch;
    if (typeof originalFetch === 'function') {
      window.fetch = async function(...args) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (typeof url === 'string' && url.includes('/api/v2/form_instances')) {
          try {
            const response = await originalFetch.apply(this, args);
            const clone = response.clone();
            const data = await clone.json();
            if (Array.isArray(data)) {
              data.forEach(instance => {
                if (instance && instance.display_rules && instance.display_rules.triggers && instance.display_rules.triggers.navigation) {
                  // Set delay to 0 so embed mounts immediately in the background
                  instance.display_rules.triggers.navigation.delay = 0;
                }
              });
              return new Response(JSON.stringify(data), {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers
              });
            }
          } catch (fetchErr) {
            // Fallback to unmodified response if parsing fails
          }
        }
        return originalFetch.apply(this, args);
      };
    }
  } catch (e) {}

  let triggeredThisVisit = false;
  let hasReachedScrollThreshold = false;
  let scrollListenerAttached = false;
  let ticking = false;

  /**
   * Safely reads and validates the stored dismissal timestamp.
   */
  function getDismissalTimestamp() {
    try {
      const raw = localStorage.getItem(STORAGE_DISMISSAL_KEY);
      if (!raw) return null;
      const ts = parseInt(raw, 10);
      if (isNaN(ts) || !isFinite(ts) || ts <= 0 || ts > Date.now()) {
        localStorage.removeItem(STORAGE_DISMISSAL_KEY);
        return null;
      }
      return ts;
    } catch (e) {
      return null;
    }
  }

  /**
   * Checks if dismissal suppression is active (within 7 days).
   * If expired, cleans up timestamp and stale session keys.
   */
  function isDismissalSuppressed() {
    const ts = getDismissalTimestamp();
    if (!ts) return false;
    const now = Date.now();
    const elapsed = now - ts;
    if (elapsed >= 0 && elapsed < SEVEN_DAYS_MS) {
      return true;
    }

    // 7 days have passed: cleanup expired timestamp
    try {
      localStorage.removeItem(STORAGE_DISMISSAL_KEY);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.endsWith('-minimized-in-session') || key.endsWith('-dismissed-in-session'))) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {}

    return false;
  }

  /**
   * Checks if visitor has already submitted the form.
   */
  function isAlreadySubmitted() {
    try {
      if (localStorage.getItem(STORAGE_SUBMISSION_KEY) === 'true') {
        return true;
      }
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.endsWith('-dismissed-after-submit')) {
          return true;
        }
      }
    } catch (e) {}
    return false;
  }

  /**
   * Records dismissal timestamp in localStorage and suppresses popup immediately.
   */
  function handleDismissal() {
    try {
      localStorage.setItem(STORAGE_DISMISSAL_KEY, Date.now().toString());
      console.log('[Shopify Forms Trigger] Popup dismissed. Suppressed for 7 days. (Tip: append ?reset_forms=true to URL to reset for testing)');
    } catch (e) {}

    const embeds = document.querySelectorAll('shopify-forms-embed');
    embeds.forEach(embed => {
      embed.setAttribute('data-forms-suppressed', 'true');
      embed.removeAttribute('data-forms-scroll-triggered');
      embed.style.setProperty('display', 'none', 'important');
    });

    detachScrollListeners();
  }

  /**
   * Marks form as permanently submitted.
   */
  function handleSubmission() {
    try {
      localStorage.setItem(STORAGE_SUBMISSION_KEY, 'true');
      localStorage.removeItem(STORAGE_DISMISSAL_KEY);
      console.log('[Shopify Forms Trigger] Form submitted! Permanently suppressed on future visits.');
    } catch (e) {}
  }

  /**
   * Calculates scroll progress percentage (0 - 100) accurately on desktop and mobile.
   * Evaluates both scroll distance and visible viewport depth.
   */
  function getScrollProgress() {
    const scrollTop = Math.max(
      window.pageYOffset || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0
    );

    const scrollHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      document.documentElement.offsetHeight,
      document.body.offsetHeight
    );

    const clientHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll <= 0) return 0;

    // Calculation 1: Scroll distance relative to total scrollable distance
    const scrollDistancePercent = (scrollTop / maxScroll) * 100;

    // Calculation 2: Viewport bottom relative to total page height
    const viewportBottomPercent = ((scrollTop + clientHeight) / scrollHeight) * 100;

    // Guard against firing on initial page load: require user to have scrolled at least 50px
    if (scrollTop < 50) return 0;

    return Math.max(scrollDistancePercent, viewportBottomPercent);
  }

  /**
   * Displays the popup when 40% scroll is reached.
   */
  function showPopup() {
    if (triggeredThisVisit || isAlreadySubmitted() || isDismissalSuppressed()) return;
    triggeredThisVisit = true;
    hasReachedScrollThreshold = true;

    console.log('[Shopify Forms Trigger] 40% scroll reached! Displaying popup.');

    const embeds = document.querySelectorAll('shopify-forms-embed');
    embeds.forEach(embed => {
      embed.setAttribute('data-forms-scroll-triggered', 'true');
      embed.removeAttribute('data-forms-suppressed');
      embed.style.removeProperty('display');
    });

    detachScrollListeners();
  }

  function detachScrollListeners() {
    if (scrollListenerAttached) {
      window.removeEventListener('scroll', handleScroll, { passive: true });
      document.removeEventListener('scroll', handleScroll, { passive: true });
      window.removeEventListener('touchmove', handleScroll, { passive: true });
      scrollListenerAttached = false;
    }
  }

  /**
   * Binds custom behavior to a shopify-forms-embed instance.
   */
  function setupEmbed(embed) {
    if (!embed) return;

    if (isAlreadySubmitted() || isDismissalSuppressed()) {
      embed.setAttribute('data-forms-suppressed', 'true');
      embed.removeAttribute('data-forms-scroll-triggered');
      embed.style.setProperty('display', 'none', 'important');
      return;
    }

    if (hasReachedScrollThreshold) {
      embed.setAttribute('data-forms-scroll-triggered', 'true');
      embed.removeAttribute('data-forms-suppressed');
      embed.style.removeProperty('display');
    } else {
      embed.removeAttribute('data-forms-scroll-triggered');
    }

    if (embed.__customTriggerSetup) return;
    embed.__customTriggerSetup = true;

    function observeShadow() {
      if (!embed.shadowRoot) return;

      const observer = new MutationObserver(() => {
        const appEmbed = embed.shadowRoot.querySelector('#app-embed');
        if (appEmbed) {
          const step = appEmbed.getAttribute('data-current-step');
          if (step === 'tease') {
            handleDismissal();
          } else if (step === 'success') {
            handleSubmission();
          }
        }
      });

      observer.observe(embed.shadowRoot, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-current-step', 'class', 'style']
      });

      embed.shadowRoot.addEventListener('submit', () => {
        handleSubmission();
      }, { capture: true, passive: true });
    }

    if (embed.shadowRoot) {
      observeShadow();
    } else {
      const checkShadowInterval = setInterval(() => {
        if (embed.shadowRoot) {
          clearInterval(checkShadowInterval);
          observeShadow();
        }
      }, 50);
      setTimeout(() => clearInterval(checkShadowInterval), 5000);
    }
  }

  /**
   * Throttled scroll handler with requestAnimationFrame.
   */
  function handleScroll() {
    if (triggeredThisVisit) return;
    if (!ticking) {
      window.requestAnimationFrame(() => {
        if (!triggeredThisVisit) {
          const progress = getScrollProgress();
          if (progress >= SCROLL_THRESHOLD_PERCENT) {
            showPopup();
          }
        }
        ticking = false;
      });
      ticking = true;
    }
  }

  /**
   * Main initialization function.
   */
  function init() {
    // Process existing embeds
    document.querySelectorAll('shopify-forms-embed').forEach(setupEmbed);

    // Watch for dynamically added embeds (always active)
    const domObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) {
            if (node.tagName === 'SHOPIFY-FORMS-EMBED') {
              setupEmbed(node);
            } else if (node.querySelectorAll) {
              node.querySelectorAll('shopify-forms-embed').forEach(setupEmbed);
            }
          }
        }
      }
    });

    if (document.body) {
      domObserver.observe(document.body, { childList: true, subtree: true });
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        domObserver.observe(document.body, { childList: true, subtree: true });
      });
    }

    // If suppressed or submitted, do not attach scroll listeners
    if (isAlreadySubmitted() || isDismissalSuppressed()) {
      const remainingDays = isDismissalSuppressed()
        ? Math.ceil((SEVEN_DAYS_MS - (Date.now() - (getDismissalTimestamp() || 0))) / (1000 * 60 * 60 * 24))
        : 0;
      console.log(`[Shopify Forms Trigger] Popup is currently suppressed (7-day dismissal active, ~${remainingDays} days left, or already submitted). Append ?reset_forms=true to URL to reset for testing.`);
      return;
    }

    // Global click listener to catch close button and "Maybe later" clicks via composedPath
    document.addEventListener('click', (e) => {
      const path = e.composedPath ? e.composedPath() : [];
      let isFromShopifyForms = false;

      for (const el of path) {
        if (!el || !el.tagName) continue;
        if (el.tagName === 'SHOPIFY-FORMS-EMBED') {
          isFromShopifyForms = true;
          break;
        }
      }

      if (!isFromShopifyForms) return;

      for (const el of path) {
        if (!el || !el.getAttribute) continue;

        const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase();
        const className = (el.className && typeof el.className === 'string') ? el.className.toLowerCase() : '';
        const text = (el.innerText || el.textContent || '').trim().toLowerCase();

        const isCloseButton = ariaLabel.includes('close') ||
                              className.includes('closebutton') ||
                              className.includes('formclosebutton');

        const isDeclineButton = text === 'maybe later' ||
                                text === 'no thanks' ||
                                text === 'not now' ||
                                text === 'decline' ||
                                text === 'close';

        if (isCloseButton || isDeclineButton) {
          handleDismissal();
          break;
        }
      }
    }, true);

    // Escape key handling
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        const visibleEmbed = document.querySelector('shopify-forms-embed[data-forms-scroll-triggered="true"]:not([data-forms-suppressed="true"])');
        if (visibleEmbed) {
          handleDismissal();
        }
      }
    });

    // Attach passive scroll and touchmove listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('touchmove', handleScroll, { passive: true });
    scrollListenerAttached = true;

    // Check immediate scroll in case page was reloaded already scrolled down
    handleScroll();
  }

  // Self-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
